import { createSlice } from "@reduxjs/toolkit";
import { IResetPassword, IStoreStatuses, STATUS_ENUM } from "types/statuses";
import { StatusGenerator } from "utils/StatusGenerator";
import {
  changePasswordThunk,
  checkLoggedUserThunk,
  confirmLoginCodeThunk,
  deleteUserThunk,
  loginCodeThunk,
  logInThunk,
  logoutThunk,
  resetPasswordRequestThunk,
  resetPasswordThunk,
  setPasswordThunk,
  updateUserThunk,
} from "./thunks";
import { LoginResponseType, LoginSteps } from "helpers/constants";
import { IAuthActionRequired } from "types/auth";
import { User } from "types/user";

interface IState {
  user: User | null;
  isUserAuthenticated: boolean;
  statuses: IStoreStatuses;
  resetPasswordInfo: IResetPassword | null;
}

const initialState: IState = {
  user: null,
  isUserAuthenticated: false,
  resetPasswordInfo: null,
  statuses: StatusGenerator.generateStatuses([
    logInThunk.typePrefix,
    logoutThunk.typePrefix,
    resetPasswordThunk.typePrefix,
    changePasswordThunk.typePrefix,
    checkLoggedUserThunk.typePrefix,
  ]),
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    resetStatus: (state) => {
      state.statuses = initialState.statuses;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(logInThunk.pending, (state) => {
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
      })
      .addCase(logInThunk.rejected, (state, { error }) => {
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.REJECTED,
          error.message,
        );
      })
      .addCase(logInThunk.fulfilled, (state, { payload }) => {
        let action;
        switch (payload.type) {
          case LoginResponseType.SUCCESS:
            state.user = payload.payload as User;
            break;
          case LoginResponseType.ACTION_REQUIRED:
            const actionContainer = payload?.payload as IAuthActionRequired;
            action = actionContainer.action;
            break;
        }
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
          undefined,
          action,
        );
      })
      .addCase(changePasswordThunk.pending, (state) => {
        state.statuses[changePasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.PENDING);
      })
      .addCase(changePasswordThunk.rejected, (state, { payload }) => {
        state.statuses[changePasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.REJECTED, payload as string);
      })
      .addCase(changePasswordThunk.fulfilled, (state) => {
        state.statuses[changePasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.FULFILLED);
      })
      .addCase(setPasswordThunk.pending, (state) => {
        state.statuses[setPasswordThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
          undefined,
          undefined,
        );
      })
      .addCase(setPasswordThunk.rejected, (state, { payload }) => {
        state.statuses[setPasswordThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.REJECTED,
          payload as string,
        );
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
          undefined,
          undefined,
        );
      })
      .addCase(setPasswordThunk.fulfilled, (state) => {
        state.statuses[setPasswordThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
        );
        state.resetPasswordInfo = null;
        state.statuses[logInThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
          undefined,
          LoginSteps.DONE,
        );
      })
      .addCase(resetPasswordThunk.pending, (state) => {
        state.statuses[resetPasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.PENDING);
      })
      .addCase(resetPasswordThunk.rejected, (state, { payload }) => {
        state.statuses[resetPasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.REJECTED, payload as string);
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.statuses[resetPasswordThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.FULFILLED);
        state.resetPasswordInfo = null;
      })
      .addCase(resetPasswordRequestThunk.pending, (state) => {
        state.statuses[resetPasswordRequestThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.PENDING);
      })
      .addCase(resetPasswordRequestThunk.rejected, (state, { payload }) => {
        state.statuses[resetPasswordRequestThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.REJECTED, payload as string);
      })
      .addCase(resetPasswordRequestThunk.fulfilled, (state) => {
        state.statuses[resetPasswordRequestThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.FULFILLED);
      })
      .addCase(checkLoggedUserThunk.pending, (state) => {
        state.statuses[checkLoggedUserThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.PENDING);
      })
      .addCase(checkLoggedUserThunk.rejected, (state, action) => {
        const message =
          (typeof action.payload === "string" && action.payload) ||
          action.error.message ||
          "User not authenticated";
        state.statuses[checkLoggedUserThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.REJECTED, message);
        state.user = null;
        state.isUserAuthenticated = false;
      })
      .addCase(checkLoggedUserThunk.fulfilled, (state, { payload }) => {
        state.statuses[checkLoggedUserThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.FULFILLED);
        state.user = payload;
        state.isUserAuthenticated = !!payload;
      })
      .addCase(logoutThunk.pending, (state) => {
        state.statuses[logoutThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.statuses[logoutThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
        );
        state.user = null;
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.statuses[updateUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
      })
      .addCase(updateUserThunk.rejected, (state, { payload }) => {
        state.statuses[updateUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.REJECTED,
          payload as string,
        );
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.statuses[updateUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
        );
      })
      .addCase(deleteUserThunk.pending, (state) => {
        state.statuses[deleteUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
      })
      .addCase(deleteUserThunk.rejected, (state, { payload }) => {
        state.statuses[deleteUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.REJECTED,
          payload as string,
        );
      })
      .addCase(deleteUserThunk.fulfilled, (state) => {
        state.statuses[deleteUserThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
        );
      })
      .addCase(loginCodeThunk.pending, (state) => {
        state.statuses[loginCodeThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.PENDING,
        );
      })
      .addCase(loginCodeThunk.rejected, (state, { payload }) => {
        state.statuses[loginCodeThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.REJECTED,
          payload as string,
        );
      })
      .addCase(loginCodeThunk.fulfilled, (state) => {
        state.statuses[loginCodeThunk.typePrefix] = StatusGenerator.setStatus(
          STATUS_ENUM.FULFILLED,
        );
        state.resetPasswordInfo = null;
      })
      .addCase(confirmLoginCodeThunk.pending, (state) => {
        state.statuses[confirmLoginCodeThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.PENDING);
      })
      .addCase(confirmLoginCodeThunk.rejected, (state, { payload }) => {
        state.statuses[confirmLoginCodeThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.REJECTED, payload as string);
      })
      .addCase(confirmLoginCodeThunk.fulfilled, (state) => {
        state.statuses[confirmLoginCodeThunk.typePrefix] =
          StatusGenerator.setStatus(STATUS_ENUM.FULFILLED);
        state.resetPasswordInfo = null;
      }),
});

export default authSlice;
